import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Loader2, RefreshCw, Trash2 } from "lucide-react";

import { useTenantSwitcher } from "@/contexts/TenantSwitcherContext";
import { ConfirmationDialog } from "@/components/shared/ConfirmationDialog";
import { LoadingPage } from "@/components/shared/LoadingPage";
import { PageHeader } from "@/components/shared/PageHeader";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { tenantMemberKeys } from "@/lib/queryKeys";
import {
  cancelTenantInvite,
  resendTenantInvite,
  type TenantPendingInvite,
} from "@/services/tenantUsersService";

export function InviteDetailPage(): JSX.Element {
  const { t } = useTranslation();
  const { inviteId } = useParams<{ inviteId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { activeTenant } = useTenantSwitcher();

  const invite = (location.state as { invite?: TenantPendingInvite } | null)
    ?.invite;
  const tenantId = activeTenant?.id ?? null;

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  const resendMutation = useMutation({
    mutationFn: () => resendTenantInvite(tenantId!, inviteId!),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: tenantMemberKeys.all(tenantId!),
      });
      queryClient.invalidateQueries({
        queryKey: tenantMemberKeys.pendingInvites(tenantId!),
      });
      toast.success(t("users.invite.resendSuccess"));
      navigate("/users");
    },
    onError: () => {
      toast.error(t("users.invite.resendError"));
    },
  });

  useEffect(() => {
    if (!invite && inviteId) {
      navigate("/users", { replace: true });
    }
  }, [invite, inviteId, navigate]);

  const cancelMutation = useMutation({
    mutationFn: () => cancelTenantInvite(tenantId!, inviteId!),
    onSuccess: () => {
      setCancelDialogOpen(false);
      queryClient.invalidateQueries({
        queryKey: tenantMemberKeys.all(tenantId!),
      });
      queryClient.invalidateQueries({
        queryKey: tenantMemberKeys.pendingInvites(tenantId!),
      });
      toast.success(t("users.invite.cancelSuccess"));
      navigate("/users");
    },
    onError: () => {
      toast.error(t("users.invite.cancelError"));
    },
  });

  if (!tenantId || !inviteId) {
    return (
      <div className="fx-content">
        <PageHeader
          title={t("users.detail.title")}
          onBack={() => navigate("/users")}
        />
        <p className="mt-4 text-sm text-muted-foreground">
          {t("users.detail.invalidContext")}
        </p>
      </div>
    );
  }

  if (!invite || invite.id !== inviteId) {
    return <LoadingPage />;
  }

  return (
    <div className="space-y-4 fx-content">
      <PageHeader
        title={invite.email}
        description={t("users.inviteDetail.subtitle")}
        onBack={() => navigate("/users")}
      />

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            {t("users.inviteDetail.infoTitle")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label className="text-muted-foreground">
                {t("users.page.columns.email")}
              </Label>
              <p className="text-sm text-foreground">{invite.email}</p>
            </div>
            <div className="space-y-2">
              <Label className="text-muted-foreground">
                {t("users.page.columns.role")}
              </Label>
              <p className="text-sm text-foreground">
                {t(`users.tenant.role.${invite.role}`)}
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-muted-foreground">
                {t("users.page.columns.status")}
              </Label>
              <div className="mt-1">
                <StatusBadge variant={invite.expired ? "danger" : "warning"}>
                  {invite.expired
                    ? t("users.page.status.expired")
                    : t("users.page.status.pending")}
                </StatusBadge>
              </div>
            </div>
            {invite.invitedByEmail && (
              <div className="space-y-2">
                <Label className="text-muted-foreground">
                  {t("users.page.invitedBy")}
                </Label>
                <p className="text-sm text-foreground">
                  {invite.invitedByEmail}
                </p>
              </div>
            )}
          </div>

          <div className="flex flex-row justify-between border-t pt-6">
            <Button
              type="button"
              variant="destructive"
              onClick={() => setCancelDialogOpen(true)}
              disabled={cancelMutation.isPending || resendMutation.isPending}
            >
              {cancelMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="mr-2 h-4 w-4" />
              )}
              {t("users.page.cancelInvite")}
            </Button>
            <Button
              type="button"
              onClick={() => resendMutation.mutate()}
              disabled={resendMutation.isPending || cancelMutation.isPending}
            >
              {resendMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              {t("users.page.resendInvite")}
            </Button>
          </div>
        </CardContent>
      </Card>

      <ConfirmationDialog
        open={cancelDialogOpen}
        onOpenChange={setCancelDialogOpen}
        title={t("users.inviteDetail.cancelTitle")}
        description={t("users.inviteDetail.cancelDescription")}
        confirmText={t("users.page.cancelInvite")}
        variant="destructive"
        onConfirm={() => cancelMutation.mutate()}
        isLoading={cancelMutation.isPending}
      />
    </div>
  );
}
